<template>
  <ModalDialog
    :header-text="`${assignment?.PlantName ?? plantName} Details`"
    :canBeClosed="!showEmailForm"
    @close-modal="closeModal"
    open
  >
    <template v-slot:content>
      <EmailForm
        v-if="showEmailForm"
        :callerType="EmailFormCallerType.RfrAssignmentsRequest"
        :emailRecipient="emailFormCompany?.RcEmail"
        :companyName="emailFormCompany?.Name"
        :plantName="assignment?.PlantName ?? plantName"
        :assignmentId="assignment?.assignmentId ?? -1"
        @formSubmitted="showEmailForm = false"
      />
      <div v-else>
        <div v-if="isMobile()" class="assignment-details-wrapper">
          <AssignmentDetailsTile
            v-for="(c, i) in companies"
            :key="i"
            :company="c"
            :allowEmail="assignment != null"
            @sendEmailClicked="a => onSendEmailClicked(a as CompanyDisplay)"
          />
          <Loader
            v-show="loading"
            class="assignment-details-loader"
            size="large"
            type="pulsing"
          />
        </div>
        <Grid
          v-else
          class="assignment-details-grid"
          :data-items="companies"
          :columns="columns"
          :scrollable="'none'"
          :loader="loading"
          data-cy="assignment-details-grid"
        >
          <template v-slot:CompanyNameCell="{ props: { dataItem } }">
            <td class="k-table-td">
              <span>{{ dataItem.Name }}</span>
              <span v-if="dataItem.IsMainCompany" class="main-company"
                >[Main]</span
              >
            </td>
          </template>
          <template v-slot:RcCell="{ props: { dataItem } }">
            <td class="k-table-td">
              <span
                v-if="assignment"
                class="rc-email-action"
                @click="() => onSendEmailClicked(dataItem as CompanyDisplay)"
                >{{ dataItem.Rc }}</span
              >
              <span v-else>{{ dataItem.Rc }}</span>
            </td>
          </template>
        </Grid>
      </div>
    </template>
  </ModalDialog>
</template>

<script setup lang="ts">
import { onBeforeMount, ref } from "vue";
import ModalDialog from "@/shared/ui/modal/ModalDialog.vue";
import { api } from "@/api";
import type {
  AssignmentWithSelection,
  GetPlantCompaniesWithFilteringRequest,
} from "@/api/rfrAssignments";
import { handleResponsePromiseAsync } from "@/shared/helpers/apiHelper";
import AssignmentDetailsTile from "./AssignmentDetailsTile.vue";
import { Loader } from "@progress/kendo-vue-indicators";
import { EmailFormCallerType } from "@/Domain/EmailFormCallerType";
import EmailForm from "@/shared/ui/forms/EmailForm.vue";
import { isMobile } from "@/shared/helpers/mobileHelper";
import { Grid, type GridColumnProps } from "@progress/kendo-vue-grid";
import type { CompanyDisplay } from "@/Domain/CompanyDisplay";

const props = defineProps<{
  assignment?: AssignmentWithSelection;
  plantName?: string;
  plantId?: number;
}>();

const emit = defineEmits<{
  (e: "closeModal"): void;
}>();

const loading = ref(false);
const emailFormCompany = ref<CompanyDisplay | null>(null);
const showEmailForm = ref(false);
const companies = ref<CompanyDisplay[]>([]);

const columns = ref<GridColumnProps[]>([
  {
    title: "Company Name",
    field: "Name",
    cell: "CompanyNameCell",
  },
  {
    title: "Plant Status",
    field: "PlantStatusAsString",
  },
  {
    title: "RC",
    field: "Rc",
    cell: "RcCell",
  },
]);

const getPlantCompaniesAsync = async function () {
  if (loading.value) return;
  loading.value = true;

  const requestParams = {
    plantId: props.assignment?.PlantID ?? props.plantId ?? 0,
    searchValue: "",
    filters: "",
    skip: 0,
    take: 100,
    sortField: "",
    ascending: true,
    valuesForFieldOnly: "",
  } as GetPlantCompaniesWithFilteringRequest;

  const assignmentPromise = api.get(
    "GetPlantCompaniesWithFiltering",
    requestParams
  );

  await handleResponsePromiseAsync(
    assignmentPromise,
    (response) => {
      companies.value = response.ListItems;
    },
    "getPlantCompaniesAsync",
    "AssignmentDetailsModal.vue"
  );

  loading.value = false;
};

const onSendEmailClicked = function (company: CompanyDisplay) {
  emailFormCompany.value = company;
  showEmailForm.value = true;
};

const closeModal = function () {
  if (showEmailForm.value) {
    showEmailForm.value = false;
    return;
  }
  emit("closeModal");
};

onBeforeMount(() => {
  getPlantCompaniesAsync();
});
</script>

<style scoped lang="scss">
.assignment-details-wrapper {
  display: flex;
  justify-content: space-between;
  padding: 10px;
  gap: 10px;
  flex-direction: column; // 1 company per row
}

.assignment-details-grid {
  .main-company {
    color: green;
    margin-left: 5px;
  }
}

.rc-email-action {
  color: #337ab7;
  cursor: pointer;
}

.assignment-details-loader {
  margin: 0 auto;
}
</style>
